(() => {
  if (customElements.get('cf-hero-search')) return;

  class CFHeroSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.input = this.querySelector('[data-cf-search-input]');
      this.clearButton = this.querySelector('[data-cf-search-clear]');
      this.closeButton = this.querySelector('[data-cf-search-close]');
      this.popup = this.querySelector('[data-cf-search-popup]');
      this.results = this.querySelector('[data-cf-search-results]');
      this.viewAll = this.querySelector('[data-cf-search-view-all]');
      this.status = this.querySelector('[data-cf-search-status]');
      this.form = this.querySelector('form[role="search"]');

      if (!this.input || !this.popup || !this.results || !this.form) return;

      this.limit = Math.min(10, Math.max(1, Number.parseInt(this.dataset.limit, 10) || 5));
      this.minCharacters = Math.min(4, Math.max(1, Number.parseInt(this.dataset.minCharacters, 10) || 2));
      this.showImages = this.dataset.showImages === 'true';
      this.showVendor = this.dataset.showVendor === 'true';
      this.showPrice = this.dataset.showPrice === 'true';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.currency = this.dataset.currency || 'USD';
      this.cache = new Map();
      this.options = [];
      this.activeIndex = -1;
      this.debounceTimer = null;
      this.requestController = null;
      this.requestId = 0;

      this.onInput = this.handleInput.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onFocus = this.handleFocus.bind(this);
      this.onClear = this.clearSearch.bind(this);
      this.onClose = this.closeSuggestions.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('search', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', this.onFocus);
      this.clearButton?.addEventListener('click', this.onClear);
      this.closeButton?.addEventListener('click', this.onClose);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.requestId += 1;
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('search', this.onInput);
      this.input?.removeEventListener('keydown', this.onKeydown);
      this.input?.removeEventListener('focus', this.onFocus);
      this.clearButton?.removeEventListener('click', this.onClear);
      this.closeButton?.removeEventListener('click', this.onClose);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      this.initialized = false;
    }

    get query() {
      return this.input.value.trim();
    }

    handleInput() {
      const query = this.query;
      this.clearButton.hidden = query.length === 0;
      this.setActiveOption(-1);
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.requestId += 1;

      if (query.length < this.minCharacters) {
        this.resetResults();
        this.close();
        this.status.textContent = '';
        return;
      }

      if (this.cache.has(query.toLocaleLowerCase(this.locale))) {
        this.renderProducts(query, this.cache.get(query.toLocaleLowerCase(this.locale)));
        return;
      }

      this.renderLoading();
      this.debounceTimer = window.setTimeout(() => this.fetchProducts(query), 220);
    }

    handleFocus() {
      if (this.query.length < this.minCharacters) return;
      if (this.options.length > 0 || this.results.childElementCount > 0) {
        this.open();
      } else {
        this.handleInput();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.popup.hidden) {
          event.preventDefault();
          this.dismissSuggestions();
        }
        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0) {
        const activeOption = this.options[this.activeIndex];
        if (activeOption) {
          event.preventDefault();
          window.location.assign(activeOption.href);
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (this.options.length === 0) return;

      event.preventDefault();
      this.open();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      let nextIndex = this.activeIndex + direction;

      if (nextIndex >= this.options.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = this.options.length - 1;
      this.setActiveOption(nextIndex, true);
    }

    handleSubmit(event) {
      if (this.query.length === 0) {
        event.preventDefault();
        this.input.focus();
      }
    }

    handleDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.dismissSuggestions();
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.dismissSuggestions();
      }, 0);
    }

    clearSearch() {
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.requestId += 1;
      this.input.value = '';
      this.clearButton.hidden = true;
      this.resetResults();
      this.status.textContent = '';
      this.close();
      this.input.focus();
    }

    closeSuggestions() {
      this.input.focus({ preventScroll: true });
      this.dismissSuggestions();
    }

    dismissSuggestions() {
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.requestId += 1;
      if (this.dataset.loading === 'true') this.resetResults();
      this.close();
    }

    resetResults() {
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      this.viewAll.hidden = true;
      this.dataset.loading = 'false';
      this.options = [];
      this.setActiveOption(-1);
    }

    async fetchProducts(query) {
      this.requestController?.abort();
      const controller = new AbortController();
      const requestId = ++this.requestId;
      this.requestController = controller;

      try {
        const root = window.Shopify?.routes?.root || this.dataset.rootUrl || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
        const endpoint = new URL(`${normalizedRoot}search/suggest.json`, window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', String(this.limit));
        endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');

        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        const payload = await response.json();
        const productResults = payload?.resources?.results?.products;
        const products = Array.isArray(productResults) ? productResults : [];
        const cacheKey = query.toLocaleLowerCase(this.locale);
        this.cache.set(cacheKey, products);

        if (this.query === query && requestId === this.requestId) this.renderProducts(query, products);
      } catch (error) {
        if (error.name !== 'AbortError' && this.query === query && requestId === this.requestId) this.renderError();
      } finally {
        if (this.requestController === controller) this.requestController = null;
      }
    }

    renderLoading() {
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < 3; index += 1) {
        const row = document.createElement('div');
        row.className = 'cf-product-hero__search-skeleton';
        row.setAttribute('aria-hidden', 'true');

        const copy = document.createElement('span');
        copy.className = 'cf-product-hero__search-skeleton-copy';

        if (this.showImages) {
          const media = document.createElement('span');
          media.className = 'cf-product-hero__search-skeleton-media';
          row.append(media, copy);
        } else {
          row.classList.add('cf-product-hero__search-skeleton--no-image');
          row.append(copy);
        }
        fragment.append(row);
      }

      this.results.replaceChildren(fragment);
      this.results.setAttribute('aria-busy', 'true');
      this.viewAll.hidden = true;
      this.dataset.loading = 'true';
      this.options = [];
      this.status.textContent = 'Loading product suggestions';
      this.open();
    }

    renderProducts(query, products) {
      this.results.removeAttribute('aria-busy');
      this.dataset.loading = 'false';
      this.setActiveOption(-1);

      if (products.length === 0) {
        this.resetResults();
        this.status.textContent = `${this.dataset.noResults || 'No matching products found.'} Press Enter to search all products.`;
        this.close();
        return;
      }

      const fragment = document.createDocumentFragment();
      let optionIndex = 0;
      products.forEach((product) => {
        const option = this.createProductOption(product, optionIndex);
        if (option) {
          fragment.append(option);
          optionIndex += 1;
        }
      });

      this.results.replaceChildren(fragment);
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));

      if (this.options.length === 0) {
        this.resetResults();
        this.status.textContent = `${this.dataset.noResults || 'No matching products found.'} Press Enter to search all products.`;
        this.close();
        return;
      }

      this.status.textContent = `${this.options.length} product suggestions available`;
      this.updateViewAll(query);
      this.open();
    }

    renderError() {
      this.resetResults();
      this.status.textContent = 'Suggestions are temporarily unavailable. Press Enter to search.';
      this.close();
    }

    createProductOption(product, index) {
      const href = this.safeStoreUrl(product.url);
      if (!href) return null;

      const option = document.createElement('a');
      option.className = 'cf-product-hero__search-result';
      option.id = `${this.results.id}-option-${index}`;
      option.href = href;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.tabIndex = -1;
      option.addEventListener('mouseenter', () => this.setActiveOption(index));

      if (this.showImages) {
        const media = document.createElement('span');
        media.className = 'cf-product-hero__search-result-media';
        const imageSource = product.featured_image?.url || product.image;

        if (imageSource) {
          const imageUrl = this.safeImageUrl(imageSource, 160);
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl;
            image.alt = '';
            image.width = 72;
            image.height = 72;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.addEventListener('error', () => {
              image.remove();
              media.classList.add('cf-product-hero__search-result-media--empty');
              media.setAttribute('aria-hidden', 'true');
            }, { once: true });
            media.append(image);
          }
        }

        if (!media.hasChildNodes()) {
          media.classList.add('cf-product-hero__search-result-media--empty');
          media.setAttribute('aria-hidden', 'true');
        }
        option.append(media);
      } else {
        option.classList.add('cf-product-hero__search-result--no-image');
      }

      const copy = document.createElement('span');
      copy.className = 'cf-product-hero__search-result-copy';

      if (this.showVendor && product.vendor) {
        const vendor = document.createElement('span');
        vendor.className = 'cf-product-hero__search-result-vendor';
        vendor.textContent = product.vendor;
        copy.append(vendor);
      }

      const title = document.createElement('span');
      title.className = 'cf-product-hero__search-result-title';
      title.textContent = product.title || '';
      copy.append(title);

      if (this.showPrice) {
        const rawPrice = product.price_min ?? product.price;
        const formattedPrice = this.formatPrice(rawPrice);
        if (formattedPrice) {
          const price = document.createElement('span');
          price.className = 'cf-product-hero__search-result-price';
          price.textContent = formattedPrice;
          copy.append(price);
        }
      }

      option.append(copy);
      return option;
    }

    safeStoreUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_error) {
        return null;
      }
    }

    safeImageUrl(value, width) {
      try {
        const url = new URL(value, window.location.origin);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
        url.searchParams.set('width', String(width));
        return url.href;
      } catch (_error) {
        return null;
      }
    }

    formatPrice(value) {
      const price = Number(value);
      if (!Number.isFinite(price)) return '';

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency
        }).format(price);
      } catch (_error) {
        return `${price.toFixed(2)} ${this.currency}`;
      }
    }

    updateViewAll(query) {
      if (!this.viewAll) return;

      try {
        const searchUrl = new URL(this.dataset.searchPath || '/search', window.location.origin);
        searchUrl.searchParams.set('q', query);
        searchUrl.searchParams.set('type', 'product');
        searchUrl.searchParams.set('options[prefix]', 'last');
        this.viewAll.href = `${searchUrl.pathname}${searchUrl.search}`;
        this.viewAll.hidden = false;
      } catch (_error) {
        this.viewAll.hidden = true;
      }
    }

    setActiveOption(index, shouldScroll = false) {
      this.options.forEach((option, optionIndex) => {
        const active = optionIndex === index;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
      });

      this.activeIndex = index;
      const activeOption = this.options[index];
      if (activeOption) {
        this.input.setAttribute('aria-activedescendant', activeOption.id);
        if (shouldScroll) activeOption.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    open() {
      this.popup.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.dataset.open = 'true';
    }

    close() {
      this.popup.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.dataset.open = 'false';
      this.setActiveOption(-1);
    }
  }

  customElements.define('cf-hero-search', CFHeroSearch);
})();
